import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { rem } from "polished";
import type { MouseEvent } from "react";
import { useCallback } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import styled from "styled-components";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import { extractColor } from "@/content-hub/editor/color-presets/utils";
import { getEventExternalIdFromEventPromotion } from "@/content-hub/event-summaries/helpers";
import type {
  EventPromotionConfig,
  PrimaryEventConfig,
} from "@/content-hub/event-summaries/types";
import {
  EVENT_PROMOTION_BLOCK_PROP,
  PRIMARY_EVENT_PAGE_PROP,
} from "@/content-hub/event-summaries/types";
import { getEventSummaryById } from "@/content-hub/store/selectors";
import type { ButtonAction } from "@/content-hub/types";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { observeScrollToUnlock } from "@/utils/toggle-page-scroll-lock";

import { ButtonSettings } from "./button-settings";

export interface ButtonProps {
  text: string;
  action: ButtonAction;
  colorSet: {
    background: string;
    text: string;
    border: string;
  };
  link?: boolean;
}

type RootNodeProps = Record<string, unknown> & {
  [PRIMARY_EVENT_PAGE_PROP]?: PrimaryEventConfig;
};

type CraftStateNode = {
  data?: {
    parent?: string;
    props?: Record<string, unknown>;
  };
  parent?: string;
  props?: Record<string, unknown>;
};

const getNodeParentId = (node?: CraftStateNode) =>
  node?.data?.parent ?? node?.parent ?? null;

const getNodeProps = (node?: CraftStateNode) =>
  node?.data?.props ?? node?.props ?? {};

async function scrollToLocation(location: "top" | "bottom") {
  await observeScrollToUnlock();
  window.scroll({
    top:
      location === "top"
        ? 0
        : document.body.scrollHeight || document.documentElement.scrollHeight,
    left: 0,
    behavior: "smooth",
  });
}
export const Button = (props: ButtonProps) => {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const flags = useFlags();
  const { contentHubLinkedEventPromotion: linkedEventFeatureEnabled } = flags;
  const {
    editorEnabled,
    primaryEventExternalId,
    eventPromotionConfig,
    eventPromotionCtaColorSet,
  } = useEditor((state) => {
    const rootProps = state.nodes[ROOT_NODE]?.data?.props as
      | RootNodeProps
      | undefined;
    const primaryEventConfig = rootProps?.[PRIMARY_EVENT_PAGE_PROP];
    const currentNode = state.nodes[nodeId] as CraftStateNode | undefined;
    // EventPromotion CTAs are authored as direct children of the parent
    // promotion block, which owns the persisted event config and CTA styling.
    const parentNodeId = getNodeParentId(currentNode);
    const parentProps = getNodeProps(
      parentNodeId
        ? (state.nodes[parentNodeId] as CraftStateNode | undefined)
        : undefined
    );

    return {
      editorEnabled: state.options.enabled,
      primaryEventExternalId: primaryEventConfig?.eventExternalId ?? null,
      eventPromotionConfig: parentProps?.[EVENT_PROMOTION_BLOCK_PROP] as
        | EventPromotionConfig
        | undefined,
      eventPromotionCtaColorSet: parentProps?.ctaColorSet as
        | ButtonProps["colorSet"]
        | undefined,
    };
  });
  const dispatch = useAppDispatch();
  const eventExternalId =
    props.action?.kind === "LinkedEvent"
      ? primaryEventExternalId
      : props.action?.kind === "Event"
      ? props.action.eventExternalId
      : props.action?.kind === "EventPromotion" && eventPromotionConfig
      ? getEventExternalIdFromEventPromotion(
          eventPromotionConfig,
          primaryEventExternalId
        )
      : null;
  const normalizedEventExternalId = eventExternalId?.trim() || null;
  const eventSummary = useAppSelector((state) =>
    normalizedEventExternalId
      ? getEventSummaryById(normalizedEventExternalId)(state)
      : null
  );
  const eventRegistrationUrl =
    eventSummary?.status === "available" ? eventSummary.registrationUrl : null;
  const isEventAction =
    props.action?.kind === "LinkedEvent" ||
    props.action?.kind === "Event" ||
    props.action?.kind === "EventPromotion";
  const shouldHideEventAction =
    isEventAction &&
    !editorEnabled &&
    (!linkedEventFeatureEnabled || !eventRegistrationUrl);
  const displayText =
    props.action?.kind === "EventPromotion"
      ? eventPromotionConfig?.ctaLabel || props.text
      : props.text;
  const displayColorSet =
    props.action?.kind === "EventPromotion" && eventPromotionCtaColorSet
      ? {
          background:
            eventPromotionCtaColorSet.background ?? props.colorSet.background,
          text: eventPromotionCtaColorSet.text ?? props.colorSet.text,
          border: eventPromotionCtaColorSet.border ?? props.colorSet.border,
        }
      : props.colorSet;
  const buttonRef =
    props.action?.kind === "EventPromotion" && editorEnabled
      ? undefined
      : connect;

  const openExternalUrl = useCallback(
    (url: string) => {
      dispatch({
        type: "[content page] submit event",
        payload: {
          event: {
            action: "url",
            url: {
              url,
            },
          },
        },
      });
      window.open(url, "_blank");
    },
    [dispatch]
  );

  const handleClick = useCallback(
    async (event?: MouseEvent<HTMLElement>) => {
      if (editorEnabled) return;

      if (!props.action) return;

      switch (props.action.kind) {
        case "None":
          break;
        case "EventSelector":
          break;
        case "LinkedEvent":
        case "Event":
        case "EventPromotion":
          if (eventRegistrationUrl) {
            openExternalUrl(eventRegistrationUrl);
          }
          break;
        case "SectionOfPage": {
          event.preventDefault();
          await observeScrollToUnlock();
          const node = document.getElementById(props.action.nodeId);
          if (node) {
            scrollIntoView(node, {
              block: "start",
              behavior: "smooth",
            });
          }
          break;
        }
        case "ExternalLink": {
          if (!props.action.href) {
            return;
          }
          openExternalUrl(props.action.href);
          break;
        }
        case "TopOfPage":
          scrollToLocation("top");
          break;
        case "BottomOfPage":
          scrollToLocation("bottom");
          break;
      }
    },
    [editorEnabled, eventRegistrationUrl, openExternalUrl, props.action]
  );

  if (shouldHideEventAction) return null;

  return (
    <StyledButton
      ref={buttonRef}
      id={nodeId}
      className="button-primitive"
      onClick={handleClick}
      colorSet={displayColorSet}
      as={props.link ? "a" : "button"}
    >
      {displayText}
    </StyledButton>
  );
};

Button.craft = {
  custom: {
    displayName: "Button",
    settingsTitle: "Button Settings",
  },
  related: {
    settings: ButtonSettings,
    leftOutlineOptions: PrimitivesFloatingOptions,
  },
};

const StyledButton = styled.button<Pick<ButtonProps, "colorSet">>`
  font-size: ${rem(16)};
  cursor: pointer;
  background-color: ${({ theme, colorSet }) =>
    extractColor(theme, colorSet.background)};
  color: ${({ theme, colorSet }) => extractColor(theme, colorSet.text)};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme, colorSet }) =>
    extractColor(theme, colorSet.border)};
  border-radius: ${({ theme }) => theme.buttonBorderRadius};
  text-decoration: none;
  padding: ${rem(12)} ${rem(24)};
`;
